import { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "components/DataTable/DataTable";
import { ZoneBreadcrumbs } from "components/ZoneBreadcrumbs/ZoneBreadcrumbs";
import { useNavigate } from "hooks/useNavigate";
import { useZoneBreadcrumbPath } from "hooks/useZoneBreadcrumbPath";
import { ISubject } from "interfaces/subject.interface";
import { IZone } from "interfaces/zone.interface";

import {
  DeviceStatus,
  deviceStatusSort,
  Sex,
} from "../../components/TableRowComponents";

type SubjectTableRow = {
  id: string;
  subjectId: string | null;
  sex: string | null;
  birthYear: string | null;
  deviceName: string | null;
  zoneName: string | null;
  deviceSerialNumber: string | null;
  deviceStatus: boolean | null;
};

type IProps = {
  subjects: ISubject[];
  zoneEntity: Record<string, IZone>;
};

export function SubjectList({ subjects, zoneEntity }: IProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const breadcrumbPath = useZoneBreadcrumbPath();

  const columns = useMemo<ColumnDef<SubjectTableRow, any>[]>(
    () => [
      {
        accessorKey: "subjectId",
        header: t("Subject Id"),
      },
      {
        accessorKey: "sex",
        header: t("Sex"),
        cell: Sex,
      },
      {
        accessorKey: "birthYear",
        header: t("Birth Year"),
      },
      {
        accessorKey: "zoneName",
        header: t("Zone"),
      },
      {
        accessorKey: "deviceName",
        header: t("Device Name / Location"),
      },
      {
        accessorKey: "deviceStatus",
        header: t("Device Status"),
        cell: DeviceStatus,
        sortingFn: deviceStatusSort,
      },
    ],
    [t],
  );

  const data = useMemo(
    (): SubjectTableRow[] =>
      subjects.map((s) => {
        const device = s.devices?.data?.[0];

        return {
          id: s.id,
          subjectId: s.identifier ?? null,
          birthYear: s.birth_year?.toString() ?? null,
          sex: s.sex ?? null,
          deviceName: device?.name ?? null,
          deviceSerialNumber: device?.serial_number ?? null,
          deviceStatus: device?.online ?? null,
          zoneName: s.zone_id ? (zoneEntity[s.zone_id]?.name ?? null) : null,
        };
      }),
    [subjects, zoneEntity],
  );

  return (
    <DataTable
      tableId={"subject-list"}
      columns={columns}
      data={data}
      getRowId={(row) => row.id}
      onRowClick={(row) => navigate(row.id)}
      topContent={<ZoneBreadcrumbs path={breadcrumbPath} />}
      footerText={t("subject_count", { count: data.length })}
    />
  );
}
